import {
  Outlet,
  createFileRoute,
  linkOptions,
  useMatch,
} from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  type CoverageFilters,
  type CoverageResponse,
  type CoverageSortingState,
  CoverageStatus,
  type DateFilter,
  InsuranceCompany,
  coverageSortingColumns,
} from 'types'
import z from 'zod'

import {
  queryCoverageCount,
  queryCoverageList,
} from '@api/coverage/coverage.queries'

import StreamingExportDialog from '@/components/export/streaming-export-dialog'
import { ExportType } from '@/components/export/streaming-export-dialog.types'
import CoverageStatusTabs from '@/components/filters/coverage-status-tabs'
import InsuranceCompanySelect from '@/components/insurance-company-select'
import LastImportSubtitle from '@/components/last-import-subtitle'
import PortfolioSelect from '@/components/portfolio-select'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import DateFilterDialog from '@/components/table/date-filter-dialog'
import CoveragesTable from '@/components/tables/coverages/coverages-table'
import { dateFilterSchema, numberFilterSchema } from '@/lib/filter'
import { cursorPaginationSchema, sortingSchema } from '@/lib/table'
import { insuranceCompanyFilterAtom } from '@/stores/insurance-company-filter.atom'
import { pageSearchAtom, storePageSearch } from '@/stores/page-search.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'
import { queryImportRunsLatest } from '@api/import-run/import-run.queries'
import { useQuery } from '@tanstack/react-query'

import SectionHeader from '../../-components/section-layout/section-header'
import CoveragesSortSelect from './-components/coverages-sort-select'

const searchSchema = z.object({
  pagination: cursorPaginationSchema,
  sorting: sortingSchema(coverageSortingColumns),
  filters: z
    .object({
      search: z.string().optional(),
      status: z
        .enum([...Object.values(CoverageStatus), 'all', 'insistence'])
        .optional(),
      insuranceCompany: z.enum(InsuranceCompany).optional(),
      policyNumber: z.string().optional(),
      insuredCompany: z.string().optional(),
      debtorId: z.string().optional(),
      debtorCompany: z.string().optional(),
      coverageId: z.string().optional(),
      requestDate: dateFilterSchema.optional(),
      decisionDate: dateFilterSchema.optional(),
      requestedAmount: numberFilterSchema.optional(),
      acceptedAmount: numberFilterSchema.optional(),
      acceptanceRate: numberFilterSchema.optional(),
    })
    .optional(),
})

type Search = z.infer<typeof searchSchema>

export const Route = createFileRoute('/app/data/coverages')({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps: { pagination, sorting } }) => {
    queryClient.prefetchQuery(
      queryCoverageList({
        ...pagination,
        sorting,
      }),
    )
    queryClient.prefetchQuery(queryCoverageCount())
  },
  beforeLoad: ({ search, cause, location }) => {
    storePageSearch(
      Route.fullPath,
      { to: Route.fullPath, search },
      location,
      cause,
    )
  },
})

function RouteComponent() {
  const { t } = useTranslation()
  const routeSearch = Route.useSearch()
  const navigate = Route.useNavigate()
  const portfolioFilter = useAtomValue(portfolioFilterAtom)
  const insuranceCompanyFilter = useAtomValue(insuranceCompanyFilterAtom)
  const { data: latestImportRuns } = useQuery(queryImportRunsLatest())

  const filters: CoverageFilters = {
    ...routeSearch.filters,
    portfolioUserUuid: portfolioFilter,
    insuranceCompany: insuranceCompanyFilter,
  }

  // If user is on a policy detail sheet,
  // don't change the search params to keep
  // the filters and sorting applied on the table.
  const pageSearch = useAtomValue(pageSearchAtom)
  const match = useMatch({
    shouldThrow: false,
    from: '/app/data/coverages/$uuid',
  })
  const tableFrozen = !!match
  const search = tableFrozen
    ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ((pageSearch[Route.fullPath]?.search as undefined | Search) ??
      routeSearch)
    : routeSearch

  const setFilters = (updater: (filter?: CoverageFilters) => CoverageFilters) =>
    navigate({
      search: (previous) => ({
        ...previous,
        filters: updater(previous.filters),
        // reset pagination to the first page on filter change
        pagination: { ...previous.pagination, cursor: undefined },
      }),
    })

  const setPagination = (
    updater: (pagination: CursorPaginationState) => CursorPaginationState,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        pagination: updater(previous.pagination),
      }),
    })

  const setSorting = (
    updater: (
      sorting?: CoverageSortingState,
    ) => CoverageSortingState | undefined,
  ) =>
    navigate({
      search: (previous) => ({
        ...previous,
        sorting: updater(previous.sorting),
      }),
    })

  const rowLink = (row: CoverageResponse) =>
    linkOptions({
      to: '/app/data/coverages/$uuid',
      params: { uuid: row.uuid },
      viewTransition: false,
    })

  const handleStatusChange = (
    status: CoverageStatus | undefined | 'insistence',
  ) => setFilters((previous) => ({ ...previous, status }))

  const handleSearchChange = (search: string) =>
    setFilters((previous) => ({ ...previous, search }))

  const setRequestDateFilter = (requestDate: DateFilter | undefined) =>
    setFilters((previous) => ({ ...previous, requestDate }))

  return (
    <div className="flex h-full grow flex-col divide-y overflow-hidden">
      <SectionHeader
        isSecondary
        title={t('data.coverages.title')}
        subtitle={
          <LastImportSubtitle
            data={latestImportRuns}
            activeInsurer={insuranceCompanyFilter}
          />
        }
      >
        <div className="flex items-center gap-2">
          <SearchBar
            tabIndex={1}
            className="max-w-62.75"
            text={filters.search}
            setText={handleSearchChange}
          />
          <InsuranceCompanySelect />
          <div className="w-47.75">
            <DateFilterDialog
              placeholder={t('table.columnFilters.date.placeholder')}
              onChange={setRequestDateFilter}
              value={filters.requestDate}
              triggerButtonProps={dateFilterTriggerButtonProps}
            />
          </div>
          <CoveragesSortSelect
            sorting={search.sorting}
            setSorting={setSorting}
          />
          <PortfolioSelect />
          <StreamingExportDialog
            type={ExportType.Coverages}
            filters={filters}
          />
        </div>
      </SectionHeader>
      <div className="flex h-full flex-col gap-4 overflow-hidden p-2">
        <CoverageStatusTabs
          filters={filters}
          value={filters.status}
          onValueChange={handleStatusChange}
        />
        <CoveragesTable
          filters={filters}
          setFilters={setFilters}
          pagination={search.pagination}
          setPagination={setPagination}
          sorting={search.sorting}
          setSorting={setSorting}
          rowLink={rowLink}
        />
      </div>
      <Outlet />
    </div>
  )
}

const dateFilterTriggerButtonProps = {
  size: 'sm',
} as const
